"use client";

import React, { useState } from 'react';
import { ChevronRight, ExternalLink, Check, X, AlertTriangle, Code, FileText, Zap } from 'lucide-react';
import TrySeozastCTA from '@/app/components/TrySeozastCTA';

const PlatformGuide = () => {
  const [activeSection, setActiveSection] = useState('');

  const platforms = [
    { id: 'webflow', name: 'Webflow', difficulty: 'Intermediate', fileUpload: false },
    { id: 'wordpress', name: 'WordPress (Self-hosted)', difficulty: 'Easy', fileUpload: true },
    { id: 'wordpresscom', name: 'WordPress.com', difficulty: 'Easy', fileUpload: false },
    { id: 'shopify', name: 'Shopify', difficulty: 'Easy', fileUpload: false },
    { id: 'wix', name: 'Wix', difficulty: 'Easy', fileUpload: false },
    { id: 'notion', name: 'Notion', difficulty: 'Basic', fileUpload: false },
    { id: 'framer', name: 'Framer', difficulty: 'Intermediate', fileUpload: false },
    { id: 'webhook', name: 'API/Webhook', difficulty: 'Advanced', fileUpload: true }
  ];

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  const StatusIcon = ({ status }: any) => {
    switch (status) {
      case 'supported': return <Check className="w-4 h-4 text-emerald-600" />;
      case 'not-supported': return <X className="w-4 h-4 text-red-500" />;
      case 'workaround': return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      default: return null;
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-12 bg-white">
      {/* Header */}
      <header className="text-center mb-16">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          Integrate SEOzast with Your Platform
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
          Complete guide to publishing SEO content generated by SEOzast on various web platforms
        </p>
        <div className="mt-6 text-sm text-gray-500">
          Updated: July 30, 2025 • By the SEOzast team
        </div>
      </header>

      {/* Table of Contents */}
      <div className="bg-gray-50 rounded-xl p-8 mb-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <FileText className="w-6 h-6 text-emerald-600 mr-3" />
          Table of Contents
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {platforms.map((platform) => (
            <button
              key={platform.id}
              onClick={() => scrollToSection(platform.id)}
              className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all hover:border-emerald-500 hover:bg-emerald-50 ${activeSection === platform.id ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-white'
                }`}
            >
              <div className="flex items-center">
                <span className="font-semibold text-gray-900">{platform.name}</span>
                <span className={`ml-3 px-2 py-1 text-xs rounded-full ${platform.difficulty === 'Easy' ? 'bg-emerald-100 text-emerald-700' :
                  platform.difficulty === 'Intermediate' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                  {platform.difficulty}
                </span>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </button>
          ))}
        </div>
      </div>

      {/* Quick Reference Table */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Comparison</h2>
        <div className="overflow-x-auto">
          <table className="w-full border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left font-semibold text-gray-900">Platform</th>
                <th className="px-6 py-4 text-center font-semibold text-gray-900">Publishing</th>
                <th className="px-6 py-4 text-center font-semibold text-gray-900">.txt Verification</th>
                <th className="px-6 py-4 text-center font-semibold text-gray-900">Meta Tags</th>
                <th className="px-6 py-4 text-center font-semibold text-gray-900">API/Webhook</th>
              </tr>
            </thead>
            <tbody>
              {[
                { name: 'Webflow', publish: 'supported', txt: 'not-supported', meta: 'supported', api: 'workaround' },
                { name: 'WordPress', publish: 'supported', txt: 'supported', meta: 'supported', api: 'supported' },
                { name: 'WordPress.com', publish: 'supported', txt: 'not-supported', meta: 'workaround', api: 'not-supported' },
                { name: 'Shopify', publish: 'supported', txt: 'not-supported', meta: 'supported', api: 'supported' },
                { name: 'Wix', publish: 'supported', txt: 'not-supported', meta: 'supported', api: 'not-supported' },
                { name: 'Notion', publish: 'workaround', txt: 'not-supported', meta: 'not-supported', api: 'not-supported' },
                { name: 'Framer', publish: 'workaround', txt: 'not-supported', meta: 'supported', api: 'not-supported' }
              ].map((row, idx) => (
                <tr key={idx} className="border-t border-gray-200">
                  <td className="px-6 py-4 font-medium text-gray-900">{row.name}</td>
                  <td className="px-6 py-4 text-center"><StatusIcon status={row.publish} /></td>
                  <td className="px-6 py-4 text-center"><StatusIcon status={row.txt} /></td>
                  <td className="px-6 py-4 text-center"><StatusIcon status={row.meta} /></td>
                  <td className="px-6 py-4 text-center"><StatusIcon status={row.api} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex items-center gap-6 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-emerald-600" />
            <span>Supported</span>
          </div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-500" />
            <span>With limitations</span>
          </div>
          <div className="flex items-center gap-2">
            <X className="w-4 h-4 text-red-500" />
            <span>Not supported</span>
          </div>
        </div>
      </div>

      {/* Platform Sections */}
      <div className="space-y-16">
        {/* Webflow */}
        <section id="webflow" className="scroll-mt-8">
          <div className="border-l-4 border-emerald-500 pl-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Webflow</h2>
            <div className="flex items-center gap-4 mb-6">
              <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm font-medium">Intermediate</span>
              <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium">No .txt files</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Check className="w-5 h-5 text-emerald-600 mr-2" />
                Option A: Webflow CMS (Recommended)
              </h3>
              <ol className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                  Create a CMS Collection named "Blog Posts"
                </li>
                <li className="flex items-start">
                  <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                  Copy title, content, and metadata from SEOzast
                </li>
                <li className="flex items-start">
                  <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                  Paste into the corresponding CMS fields
                </li>
                <li className="flex items-start">
                  <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</span>
                  Customize the slug and publish
                </li>
              </ol>
              <div className="mt-4 p-3 bg-emerald-100 rounded-lg">
                <p className="text-sm text-emerald-800">✅ Enables layout automation with CMS templates</p>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <AlertTriangle className="w-5 h-5 text-yellow-500 mr-2" />
                Option B: Static Pages
              </h3>
              <ol className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="bg-gray-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                  Go to Pages → Create New Page
                </li>
                <li className="flex items-start">
                  <span className="bg-gray-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                  Paste the content in the designer
                </li>
                <li className="flex items-start">
                  <span className="bg-gray-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                  Configure Title Tag and Meta Description
                </li>
                <li className="flex items-start">
                  <span className="bg-gray-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</span>
                  Publish the page
                </li>
              </ol>
              <div className="mt-4 p-3 bg-yellow-100 rounded-lg">
                <p className="text-sm text-yellow-800">⚠️ Manual process, does not support file imports</p>
              </div>
            </div>

            {/* <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Zap className="w-5 h-5 text-blue-600 mr-2" />
                Webflow Plugin
              </h3>
              <p className="text-gray-700 mb-4">
                Publish SEOzast articles directly from your Webflow dashboard — no copy-paste needed.
              </p>
              <div className="bg-blue-100 rounded-lg p-3">
                <p className="text-sm text-blue-800 font-medium">✅ Now available</p>
              </div>
            </div> */}
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <X className="w-5 h-5 text-red-500 mr-2" />
              Domain Verification
            </h3>
            <p className="text-gray-700 mb-4">Webflow does not allow uploading .txt files to the root. Use this alternative:</p>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Solution: Meta Tag</h4>
              <ol className="space-y-2 text-gray-700 mb-4">
                <li>1. Go to Site Settings → Custom Code → Head</li>
                <li>2. Paste the verification meta tag:</li>
              </ol>
              <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm overflow-x-auto">
                {`<meta name="bing-site-verification" content="your-code-here" />`}
              </pre>
              <p className="text-sm text-emerald-700 mt-2">✅ Accepted by Bing and other search engines</p>
            </div>
          </div>
        </section>

        {/* WordPress */}
        <section id="wordpress" className="scroll-mt-8">
          <div className="border-l-4 border-emerald-500 pl-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">WordPress (Self-hosted)</h2>
            <div className="flex items-center gap-4 mb-6">
              <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">Easy</span>
              <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">Full support</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Manual Publishing</h3>
              <ol className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                  Go to Posts → Add New
                </li>
                <li className="flex items-start">
                  <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                  Paste title and content from SEOzast
                </li>
                <li className="flex items-start">
                  <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                  Configure slug, excerpt, and featured image
                </li>
                <li className="flex items-start">
                  <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</span>
                  Add SEO metadata (Yoast/RankMath)
                </li>
                <li className="flex items-start">
                  <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">5</span>
                  Publish the article
                </li>
              </ol>
            </div>

            {/* <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Zap className="w-5 h-5 text-blue-600 mr-2" />
                WordPress Plugin
              </h3>
              <p className="text-gray-700 mb-4">
                Publish SEOzast articles directly from your WordPress dashboard — no copy-paste needed.
              </p>
              <div className="bg-blue-100 rounded-lg p-3">
                <p className="text-sm text-blue-800 font-medium">✅ Now available</p>
              </div>
            </div> */}
          </div>

          <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-lg p-6">
            <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
              <Check className="w-5 h-5 text-emerald-600 mr-2" />
              Domain Verification
            </h4>
            <p className="text-gray-700">
              WordPress allows uploading .txt files directly to the root directory via FTP or the hosting file manager.
            </p>
          </div>
        </section>

        {/* Shopify */}
        <section id="shopify" className="scroll-mt-8">
          <div className="border-l-4 border-emerald-500 pl-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Shopify</h2>
            <div className="flex items-center gap-4 mb-6">
              <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">Easy</span>
              <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">Integrated SEO</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Blog Publishing</h3>
              <ol className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                  Go to Online Store → Blog Posts → Add blog post
                </li>
                <li className="flex items-start">
                  <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                  Paste title and content from SEOzast
                </li>
                <li className="flex items-start">
                  <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                  Configure SEO metadata and handle (URL)
                </li>
                <li className="flex items-start">
                  <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</span>
                  Publish the article
                </li>
              </ol>
              <div className="mt-4 p-3 bg-emerald-100 rounded-lg">
                <p className="text-sm text-emerald-800">✅ Includes SEO metadata and sitemaps by default</p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Zap className="w-5 h-5 text-blue-600 mr-2" />
                Shopify Plugin
              </h3>
              <p className="text-gray-700 mb-4">
                Publish SEOzast articles directly from your Shopify dashboard — no copy-paste needed.
              </p>
              <div className="bg-blue-100 rounded-lg p-3">
                <p className="text-sm text-blue-800 font-medium">✅ Now available</p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
              <AlertTriangle className="w-5 h-5 text-yellow-500 mr-2" />
              Domain Verification
            </h4>
            <p className="text-gray-700">
              Shopify does not allow .txt files in the root. Use meta tag verification in the theme.
            </p>
          </div>
        </section>

        {/* Wix */}
        <section id="wix" className="scroll-mt-8">
          <div className="border-l-4 border-emerald-500 pl-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Wix</h2>
            <div className="flex items-center gap-4 mb-6">
              <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">Easy</span>
              <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium">No .txt files</span>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <p className="text-gray-700 mb-4">
              Wix has a built-in blog editor where you can easily paste SEOzast content.
            </p>
            <ol className="space-y-2 text-gray-700">
              <li>1. Go to Wix Dashboard → Blog → Create New Post</li>
              <li>2. Paste title and content from SEOzast</li>
              <li>3. Configure SEO title, meta description, and slug</li>
              <li>4. Publish the article</li>
            </ol>
          </div>
        </section>

        {/* Notion */}
        <section id="notion" className="scroll-mt-8">
          <div className="border-l-4 border-emerald-500 pl-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Notion</h2>
            <div className="flex items-center gap-4 mb-6">
              <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium">Basic</span>
              <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm font-medium">Limited SEO</span>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <p className="text-gray-700 mb-4">
              Notion is great for drafts but not optimized for SEO unless additional tools are used.
            </p>
            <ol className="space-y-2 text-gray-700 mb-4">
              <li>1. Export your article from SEOzast in Markdown</li>
              <li>2. Paste the content into a new Notion page</li>
              <li>3. Optionally, use Super.so or Potion for SEO hosting</li>
            </ol>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <p className="text-sm text-yellow-800">⚠️ Notion pages are not SEO-optimized without a custom domain</p>
            </div>
          </div>
        </section>

        {/* Framer */}
        <section id="framer" className="scroll-mt-8">
          <div className="border-l-4 border-emerald-500 pl-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Framer</h2>
            <div className="flex items-center gap-4 mb-6">
              <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm font-medium">Intermediate</span>
              <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm font-medium">No native CMS</span>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <p className="text-gray-700 mb-4">
              Framer does not yet have a native CMS, but you can manually integrate articles.
            </p>
            <ol className="space-y-2 text-gray-700">
              <li>1. Export the article as HTML or Markdown</li>
              <li>2. Create a new page in Framer</li>
              <li>3. Use a Rich Text block or custom code</li>
              <li>4. Configure metadata in Page Settings (SEO tab)</li>
              <li>5. Publish the page</li>
            </ol>
            <div className="mt-4 p-3 bg-red-100 rounded-lg">
              <p className="text-sm text-red-800">❌ No file uploads or API posting available yet</p>
            </div>
          </div>
        </section>

        {/* API/Webhook */}
        <section id="webhook" className="scroll-mt-8">
          <div className="border-l-4 border-emerald-500 pl-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">API/Webhook Integration</h2>
            <div className="flex items-center gap-4 mb-6">
              <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium">Advanced</span>
              <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">Full automation</span>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Code className="w-5 h-5 text-blue-600 mr-2" />
              Custom Integration
            </h3>
            <p className="text-gray-700 mb-4">
              SEOzast supports webhooks that allow auto-publishing content to any CMS with an API.
            </p>
            <ol className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                Go to project settings → Add Webhook URL
              </li>
              <li className="flex items-start">
                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                Configure your backend to receive POST requests
              </li>
              <li className="flex items-start">
                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                Auto-save or publish articles in your system
              </li>
            </ol>
            <div className="mt-4 p-3 bg-blue-100 rounded-lg">
              <p className="text-sm text-blue-800">✅ Perfect for headless CMS like Strapi, Sanity, or Contentful</p>
            </div>
          </div>
        </section>

        {/* WordPress.com */}
        <section id="wordpresscom" className="scroll-mt-8">
          <div className="border-l-4 border-emerald-500 pl-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">WordPress.com</h2>
            <div className="flex items-center gap-4 mb-6">
              <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">Easy</span>
              <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm font-medium">Limited features</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Manual Publishing</h3>
              <p className="text-gray-700 mb-4">
                Unlike self-hosted WordPress, WordPress.com restricts plugins and advanced integrations.
              </p>
              <ol className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                  Go to Posts → Add New
                </li>
                <li className="flex items-start">
                  <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                  Paste article content manually
                </li>
                <li className="flex items-start">
                  <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                  Use built-in SEO settings if available (under Post Settings)
                </li>
                <li className="flex items-start">
                  <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</span>
                  Publish
                </li>
              </ol>
              <div className="mt-4 p-3 bg-yellow-100 rounded-lg">
                <p className="text-sm text-yellow-800">⚠️ You can’t upload .txt verification files; use meta tag workaround for Bing</p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Zap className="w-5 h-5 text-blue-600 mr-2" />
                WordPress Plugin
              </h3>
              <p className="text-gray-700 mb-4">
                Publish SEOzast articles directly from your WordPress dashboard — no copy-paste needed.
              </p>
              <div className="bg-blue-100 rounded-lg p-3">
                <p className="text-sm text-blue-800 font-medium">✅ Now available</p>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <div className="bg-gray-100 mt-16 p-6 rounded-lg shadow-sm text-center">
          <h3 className="text-lg font-semibold mb-4">Want to auto-publish your articles?</h3>
          <TrySeozastCTA />
        </div>
      </div>
    </div>
  );
};

export default PlatformGuide;